import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { hasCookie, getCookie } from 'cookies-next';

import { authService } from '@/services/authService';
import { AuthState, LoginCredentials, User, LoginResponse } from '@/types/auth';

// Initialize state from cookies
const userCookie = hasCookie('user') ? (getCookie('user') as string) : null;
const initialUser = userCookie ? JSON.parse(userCookie) : null;
const expiresInCookie = hasCookie('expiresIn')
  ? parseInt(getCookie('expiresIn') as string, 10)
  : null;

const initialState: AuthState = {
  user: initialUser,
  token: (getCookie('session') as string) || null,
  expiresIn: expiresInCookie,
  isAuthenticated: !!initialUser,
  isLoading: false,
  checkEmailLoading: false,
  error: null,
  isSignUp: false,
  requestAction: null,
};

interface ResetPasswordData {
  otp: string;
  password: string;
  confirm_password: string;
  token: string;
}

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        const status = (error as any).status || (error as any).response?.status;

        switch (status) {
          case 401:
            return rejectWithValue('Incorrect username or password');
          case 500:
            return rejectWithValue('Something went wrong, please try again');
          case 400:
            return rejectWithValue('Incorrect username or password');
          case 403:
            return rejectWithValue('Your account is not active');
          case 404:
            return rejectWithValue('User not found');
          case 409:
            return rejectWithValue('User already exists');
          case 429:
            return rejectWithValue('Too many attempts, please try again later');
          case 422:
            return rejectWithValue('Invalid input');
          default:
            return rejectWithValue(error.message || 'Login failed');
        }
      }
      return rejectWithValue('Login failed');
    }
  },
);

export const googleLogin = createAsyncThunk('auth/googleLogin', async (_, { rejectWithValue }) => {
  try {
    // Get the Google auth URL from your backend
    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/google/url`);
    const { url } = await response.json();

    // Redirect to Google's auth page
    window.location.href = url;
    return null;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Google login failed');
  }
});

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: ResetPasswordData, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(data);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Password reset failed');
    }
  },
);

export const checkEmail = createAsyncThunk(
  'auth/checkEmail',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authService.checkEmail(email);
      return response.exists;
    } catch (error) {
      return rejectWithValue('Failed to check email');
    }
  },
);

export const signOut = createAsyncThunk('auth/signOut', async () => {
  // Clear cookies through API route
  await fetch('/api/auth/signout', {
    method: 'POST',
  });
  return null;
});

export const signupUser = createAsyncThunk(
  'auth/signup',
  async (formData: { firstName: string; lastName: string; email: string }, { rejectWithValue }) => {
    try {
      const response = await authService.signup(formData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create account');
    }
  },
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ email, otp }: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOTP(email, otp);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to verify OTP');
    }
  },
);

export const requestLoginOTP = createAsyncThunk(
  'auth/requestLoginOTP',
  async (email: string, { rejectWithValue }) => {
    try {
      await authService.requestLoginOTP(email);
      return email;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send OTP');
    }
  },
);

export const loginWithOTP = createAsyncThunk(
  'auth/loginWithOTP',
  async ({ email, otp }: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await authService.loginWithOTP(email, otp);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to login with OTP');
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.expiresIn = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setGoogleAuthSuccess: (state, action: { payload: LoginResponse }) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.expiresIn = action.payload.expiresIn;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
    setIsSignUp: (state, action) => {
      state.isSignUp = action.payload;
    },
    setRequestAction: (state, action) => {
      state.requestAction = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.expiresIn = action.payload.expiresIn;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(googleLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Check Email
      .addCase(checkEmail.pending, (state) => {
        state.checkEmailLoading = true;
        state.error = null;
      })
      .addCase(checkEmail.fulfilled, (state, action) => {
        state.checkEmailLoading = false;
      })
      .addCase(checkEmail.rejected, (state, action) => {
        state.checkEmailLoading = false;
        state.error = action.payload as string;
      })
      .addCase(signupUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.expiresIn = action.payload.expiresIn;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(signOut.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.expiresIn = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = 'Failed to sign out';
      })
      .addCase(requestLoginOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestLoginOTP.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestLoginOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(loginWithOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.expiresIn = action.payload.expiresIn;
        state.isAuthenticated = true;
      })
      .addCase(loginWithOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setUser,
  setLoading,
  setError,
  logout,
  setGoogleAuthSuccess,
  setIsSignUp,
  setRequestAction,
} = authSlice.actions;
export default authSlice.reducer;
